import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatPrice } from "@/lib/api";
import { DASH } from "@/constants/testIds";
import { Plus, Home, Sparkles, Heart, MessageSquare, BadgeCheck, Pencil, Trash2, Rocket, Eye, AlertCircle, TrendingUp, Bell, BellOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const push = usePushNotifications(user);
  const [stats, setStats] = useState({ total: 0, active: 0, boosted: 0, favorites_received: 0, messages_received: 0, total_views: 0, week_views: 0 });
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      const [{ data: s }, { data: list }] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/listings-stats"),
      ]);
      setStats(s);
      setItems(list);
    })();
  }, [user, loading, navigate]);

  const handleBoost = async (id) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "boost",
        listing_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Erreur paiement boost");
    }
  };

  const handleSubscribePro = async () => {
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "pro_subscription",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Erreur paiement abonnement");
    }
  };

  const refreshList = async () => {
    const { data } = await api.get("/dashboard/listings-stats");
    setItems(data);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      setItems((p) => p.filter((l) => l.id !== id));
      toast.success("Annonce supprimée");
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  if (!user) return null;

  return (
    <Layout withFooter={false}>
      <div data-testid={DASH.container} className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">{t("dashboard.overline")}</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-2">
              {t("dashboard.greeting", { name: user.name })}
              {user.badge === "pro_verified" && <BadgeCheck className="w-7 h-7 text-[#11783A]" />}
            </h1>
            <p className="text-muted-foreground mt-1">
              {user.role === "pro" ? `${t("dashboard.pro")} · ${user.company || "—"}` : t("dashboard.particulier")} · {user.country}
            </p>
          </div>
          <Button
            onClick={() => navigate("/dashboard/new")}
            data-testid={DASH.newListingBtn}
            className="rounded-full bg-[#D6202C] hover:bg-[#B5181F] px-6 h-11"
          >
            <Plus className="w-4 h-4 me-1" /> {t("dashboard.new_listing")}
          </Button>
        </div>

        {/* Pro tools row: KYC + Push */}
        <div className="flex flex-wrap gap-3 mb-6">
          {user.role === "pro" && (
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/kyc")}
              data-testid="dashboard-kyc-btn"
              className="rounded-full"
            >
              <ShieldCheck className="w-4 h-4 me-1.5" />
              Vérification KYC
            </Button>
          )}
          {push.supported && (
            <Button
              variant="outline"
              onClick={async () => {
                if (push.subscribed) {
                  await push.unsubscribe();
                  toast.success("Notifications désactivées");
                } else {
                  const ok = await push.subscribe();
                  if (ok) toast.success("Notifications activées ✓");
                  else if (push.permission === "denied") toast.error("Autorisez les notifications dans votre navigateur");
                }
              }}
              disabled={push.busy}
              data-testid="dashboard-push-btn"
              className="rounded-full"
            >
              {push.subscribed ? (
                <><BellOff className="w-4 h-4 me-1.5" /> Désactiver les notifications</>
              ) : (
                <><Bell className="w-4 h-4 me-1.5" /> Activer les notifications</>
              )}
            </Button>
          )}
        </div>

        {user.role !== "pro" && (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-[#11783A] to-[#0A5C2C] text-white p-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#F4B400] text-[#1A1A1A] flex items-center justify-center shrink-0">
                <BadgeCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading text-lg font-medium">{t("dashboard.become_pro_title")}</div>
                <div className="text-sm text-white/70">{t("dashboard.become_pro_desc")}</div>
              </div>
            </div>
            <Button
              onClick={handleSubscribePro}
              data-testid="dashboard-subscribe-pro"
              className="rounded-full bg-[#F4B400] hover:bg-[#E0A500] text-[#1A1A1A] font-medium"
            >
              <Rocket className="w-4 h-4 me-1" /> {t("dashboard.become_pro_btn")}
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <StatBox testId={DASH.statTotal} icon={<Home />} label={t("dashboard.stats_total")} value={stats.total} />
          <StatBox testId={DASH.statActive} icon={<Sparkles />} label={t("dashboard.stats_active")} value={stats.active} />
          <StatBox testId={DASH.statBoosted} icon={<Rocket />} label={t("dashboard.stats_boosted")} value={stats.boosted} highlight />
          <StatBox testId="stat-views" icon={<Eye />} label={`${t("dashboard.stats_views")} ${t("dashboard.stats_views_week")}`} value={`${stats.week_views} / ${stats.total_views}`} />
          <StatBox testId={DASH.statFavorites} icon={<Heart />} label={t("dashboard.stats_favorites")} value={stats.favorites_received} />
          <StatBox testId={DASH.statMessages} icon={<MessageSquare />} label={t("dashboard.stats_messages")} value={stats.messages_received} />
        </div>

        {/* Listings */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <h2 className="font-heading text-xl font-medium">{t("dashboard.my_listings")}</h2>
            <span className="text-sm text-muted-foreground">{items.length} annonce(s)</span>
          </div>

          {items.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <p className="mb-4">{t("dashboard.no_listings")}</p>
              <Button onClick={() => navigate("/dashboard/new")} className="bg-[#D6202C] hover:bg-[#B5181F] rounded-full">
                <Plus className="w-4 h-4 me-1" /> {t("dashboard.create_first")}
              </Button>
            </div>
          ) : (
            <div data-testid={DASH.listingsTable} className="divide-y divide-[#E5E5E5]">
              {items.map((l) => {
                const s = l.stats || {};
                return (
                  <div key={l.id} className="p-4 hover:bg-[#FAFAFA]">
                    <div className="flex items-center gap-4">
                      <img src={l.photos?.[0]} alt="" className="w-20 h-20 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/listings/${l.id}`} className="font-heading font-medium hover:text-[#D6202C] truncate block">
                          {l.title}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {l.city} · {l.property_type} · {l.type} · {Math.round(l.surface)} m²
                        </div>
                        <div className="font-heading font-semibold mt-1 text-[#D6202C]">
                          {formatPrice(l.price, l.currency, l.type)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {l.is_boosted ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-[#F4B400] to-[#D6202C] text-white">{t("dashboard.boosted_label")}</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBoost(l.id)}
                            data-testid={DASH.boostBtn}
                            className="rounded-full"
                          >
                            <Rocket className="w-3.5 h-3.5 me-1" /> {t("dashboard.boost")}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dashboard/edit/${l.id}`)}
                          data-testid={DASH.editBtn}
                          className="rounded-full"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={DASH.deleteBtn} className="rounded-full text-red-600 hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("dashboard.confirm_delete_title")}</AlertDialogTitle>
                              <AlertDialogDescription>{t("dashboard.confirm_delete_desc")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("dashboard.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(l.id)} className="bg-red-600 hover:bg-red-700">
                                {t("dashboard.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    {/* Per-listing metrics */}
                    <div className="flex items-center gap-5 mt-3 ms-24 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1" data-testid="listing-views">
                        <Eye className="w-3.5 h-3.5 text-[#11783A]" />
                        <strong className="text-[#1A1A1A]">{s.views_total ?? 0}</strong> {t("dashboard.views_label").toLowerCase()}
                      </span>
                      <span className="flex items-center gap-1" data-testid="listing-contacts">
                        <MessageSquare className="w-3.5 h-3.5 text-[#11783A]" />
                        <strong className="text-[#1A1A1A]">{s.messages ?? 0}</strong> {t("dashboard.messages_label").toLowerCase()}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5 text-[#11783A]" />
                        <strong className="text-[#1A1A1A]">{s.conversion_rate ?? 0}%</strong> {t("dashboard.conversion_label").toLowerCase()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5 text-[#11783A]" />
                        <strong className="text-[#1A1A1A]">{s.favorites ?? 0}</strong>
                      </span>
                    </div>

                    {s.recommendation && (
                      <div
                        data-testid="listing-recommendation"
                        className="ms-24 mt-3 px-3 py-2 rounded-xl bg-[#FEF8E7] border border-[#F4B400]/40 text-[#7A5D00] text-xs flex items-center gap-2"
                      >
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>
                          {s.recommendation === "low_views_boost"
                            ? t("dashboard.recommendation_low_views")
                            : t("dashboard.recommendation_low_conversion")}
                        </span>
                        {!l.is_boosted && (
                          <Button
                            size="sm"
                            onClick={() => handleBoost(l.id)}
                            className="ms-auto bg-[#D6202C] hover:bg-[#B5181F] rounded-full h-7 text-[11px]"
                          >
                            <Rocket className="w-3 h-3 me-1" /> {t("dashboard.boost")}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const StatBox = ({ icon, label, value, highlight, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-5 border ${
      highlight ? "bg-gradient-to-br from-[#F4B400] to-[#D6202C] text-white border-transparent" : "bg-white border-[#E5E5E5]"
    }`}
  >
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${highlight ? "bg-white/20" : "bg-[#FEF2F3] text-[#D6202C]"}`}>
      {icon}
    </div>
    <div className="font-heading text-3xl font-semibold leading-none">{value}</div>
    <div className={`text-xs mt-1 ${highlight ? "text-white/80" : "text-muted-foreground"}`}>{label}</div>
  </div>
);
